import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CompositionEvent,
  type KeyboardEvent,
} from 'react'

/**
 * Canvas text-input / IME guards.
 *
 * Controlled inputs MUST keep a local draft that updates on every `onChange`
 * (including during IME composition). Skipping React updates while
 * `value={persisted}` stays controlled causes the classic mid-composition
 * reset — pinyin intermediates get "committed", then Chinese appends
 * (e.g. `aa'da'd'fad'fa大发`), or the field appears uneditable.
 */

export function isCanvasEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [contenteditable=""], .ant-select, .ant-input, .ant-input-affix-wrapper, .ant-input-textarea',
    ),
  )
}

export function isCanvasImeComposingEvent(e: KeyboardEvent | globalThis.KeyboardEvent): boolean {
  const native = 'nativeEvent' in e ? e.nativeEvent : e
  return Boolean(native.isComposing || native.keyCode === 229)
}

/** Canvas shortcuts / Delete / Escape must skip when typing or composing. */
export function shouldIgnoreCanvasShortcutEvent(e: KeyboardEvent | globalThis.KeyboardEvent): boolean {
  if (isCanvasImeComposingEvent(e)) return true
  return isCanvasEditableTarget(e.target)
}

export function stopCanvasEditableKeyPropagation(e: KeyboardEvent): void {
  e.stopPropagation()
}

export function useCanvasImeSafeTextHandlers(args: {
  value: string
  onValueChange: (next: string) => void
  onAfterValueChange?: () => void
}) {
  const [draft, setDraft] = useState(args.value)
  const composingRef = useRef(false)
  const focusedRef = useRef(false)
  const lastEmittedRef = useRef(args.value)
  const onValueChangeRef = useRef(args.onValueChange)
  const onAfterValueChangeRef = useRef(args.onAfterValueChange)
  onValueChangeRef.current = args.onValueChange
  onAfterValueChangeRef.current = args.onAfterValueChange

  // Sync external → draft only when safe.
  // Never overwrite while composing. While focused, only accept our own emit echo.
  useEffect(() => {
    if (composingRef.current) return
    if (focusedRef.current) {
      if (args.value === lastEmittedRef.current) {
        setDraft(args.value)
      }
      return
    }
    setDraft(args.value)
  }, [args.value])

  const emit = useCallback((next: string) => {
    lastEmittedRef.current = next
    onValueChangeRef.current(next)
    onAfterValueChangeRef.current?.()
  }, [])

  const onCompositionStart = useCallback(() => {
    composingRef.current = true
  }, [])

  const onCompositionEnd = useCallback(
    (e: CompositionEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      composingRef.current = false
      const next = e.currentTarget.value
      setDraft(next)
      emit(next)
    },
    [emit],
  )

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value
      // Always update local draft so controlled `value` matches the DOM during IME.
      setDraft(next)
      if (composingRef.current) return
      emit(next)
    },
    [emit],
  )

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    stopCanvasEditableKeyPropagation(e)
  }, [])

  const onFocus = useCallback(() => {
    focusedRef.current = true
  }, [])

  const onBlur = useCallback(() => {
    focusedRef.current = false
    composingRef.current = false
    setDraft((current) => {
      if (current !== lastEmittedRef.current) {
        lastEmittedRef.current = current
        onValueChangeRef.current(current)
        onAfterValueChangeRef.current?.()
      }
      return current
    })
  }, [])

  const setValue = useCallback(
    (next: string) => {
      composingRef.current = false
      setDraft(next)
      emit(next)
    },
    [emit],
  )

  /** Sync draft from undo/redo without emitting onValueChange (preserves redo stack). */
  const applyExternalValue = useCallback((next: string) => {
    composingRef.current = false
    lastEmittedRef.current = next
    setDraft(next)
  }, [])

  return {
    inputProps: {
      value: draft,
      onCompositionStart,
      onCompositionEnd,
      onChange,
      onKeyDown,
      onFocus,
      onBlur,
    },
    setValue,
    applyExternalValue,
  }
}
